import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsLeftRight } from '@fortawesome/pro-light-svg-icons/faArrowsLeftRight';
import { TypedMemo } from '../../common/types';
import { EMBED_HEIGHT } from './embedSnippet';
import styles from './EmbedPreviewStage.module.scss';
import { usePreviewPageLang } from '../smlPreview/usePreviewPageLang';

interface EmbedPreviewStageProps {
  embedUrl: string;
  /** The width the artist chose. The frame is rendered at it, then scaled to fit. */
  width: number;
  /** The frame's accessible name, the same one the snippet carries. */
  title: string;
}

/**
 * The live page, at the chosen width, inside a stand-in post.
 *
 * The frame is rendered at its real size and scaled down, so what the artist
 * judges is the widget's proportion against a column of text — which is the
 * only question the width slider actually asks. It is never scaled up: a frame
 * narrower than the column is shown at its own size, as a reader would see it.
 */
const EmbedPreviewStageComponent: React.FC<EmbedPreviewStageProps> = ({ embedUrl, width, title }) => {
  const intl = useIntl();
  // The frame shows the page in the app language. The snippet keeps the clean address.
  const withLang = usePreviewPageLang();
  const frameUrl = useMemo(() => withLang(embedUrl), [embedUrl, withLang]);
  const boxRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const element = boxRef.current;
    if (element === null) {
      return undefined;
    }
    const observer = new ResizeObserver(([entry]) => {
      if (entry === undefined) {
        return;
      }
      setBox({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const widthScale = box.width > 0 ? box.width / width : 1;
  const heightScale = box.height > 0 ? box.height / EMBED_HEIGHT : 1;
  const scale = Math.min(1, widthScale, heightScale);

  return (
    <div className={styles.stage}>
      <div className={styles.post}>
        <div className={styles.lines}>
          <div className={clsx(styles.line, styles.lineHead)} />
          <div className={styles.line} />
          <div className={clsx(styles.line, styles.lineShort)} />
        </div>

        <div ref={boxRef} className={styles.viewportBox}>
          <div className={styles.viewport} style={{ width: width * scale, height: EMBED_HEIGHT * scale }}>
            <div className={styles.frame} style={{ width, height: EMBED_HEIGHT, transform: `scale(${scale})` }}>
              <iframe
                className={styles.iframe}
                title={title}
                src={frameUrl}
                width={width}
                height={EMBED_HEIGHT}
                allow="encrypted-media"
              />
            </div>
          </div>
        </div>

        <div className={styles.lines}>
          <div className={styles.line} />
          <div className={clsx(styles.line, styles.lineTiny)} />
        </div>
      </div>

      <p className={styles.caption}>
        <FontAwesomeIcon icon={faArrowsLeftRight} />
        {intl.formatMessage(
          {
            id: 'app.sml.embed.width.caption',
            defaultMessage: 'Caps at {width}px, and shrinks to fit a narrower column.',
          },
          { width }
        )}
      </p>
    </div>
  );
};

export const EmbedPreviewStage = TypedMemo(EmbedPreviewStageComponent);
